import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { apiResources } from '../api/api.resources';

export interface DashboardOverviewResponse {
    success: boolean;
    data: {
        period: {
            month: number;
            year: number;
            label: string;
        };
        members: {
            total: number;
            trend: {
                value: number | null;
                percentage: number | null;
                direction: 'up' | 'down';
                label: string;
            };
        };
        tithes: {
            total: number;
            trend: {
                value: number | null;
                percentage: number | null;
                direction: 'up' | 'down';
                label: string;
            };
        };
        purchases: {
            total: number;
            trend: {
                value: number | null;
                percentage: number | null;
                direction: 'up' | 'down';
                label: string;
            };
        };
        exits: {
            total: number;
            trend: {
                value: number | null;
                percentage: number | null;
                direction: 'up' | 'down';
                label: string;
            };
        };
    };
}

export interface EntriesVsExitsResponse {
    success: boolean;
    data: {
        period: {
            months: number;
            startDate: string;
            endDate: string;
        };
        chart: {
            categories: string[];
            series: {
                entries: number[];
                exits: number[];
            };
        };
        summary: {
            totalEntries: number;
            totalExits: number;
            balance: number;
            averageMonthlyBalance: number;
        };
    };
}

export interface MemberEngagementResponse {
    success: boolean;
    data: {
        currentMonth: {
            totalMembers: number;
            activeTithers: number;
            engagementRate: number;
            averageContributionsPerMember: number;
            totalContributions: number;
        };
        previousMonth: {
            totalMembers: number;
            activeTithers: number;
            engagementRate: number;
            averageContributionsPerMember: number;
            totalContributions: number;
        };
        trends: {
            membersGrowth: number;
            tithersGrowth: number;
            engagementGrowth: number;
            contributionsGrowth: number;
        };
        date: string;
    };
}

/**
 * Contadores de uso alimentados pelo backend juntamente com o armazenamento.
 * Endpoint: GET /v1/dashboard/storage-usage
 */
export interface StorageCounters {
    documents_processed: number;
    receipts_processed: number;
    meetings_transcribed: number;
    members_count: number;
}

/**
 * Uso de armazenamento do tenant (bucket de arquivos + banco de dados).
 * Endpoint: GET /v1/dashboard/storage-usage
 *
 * Contrato (payload):
 * {
 *   "plan_storage_gb": 10.0, "total_bytes": 10737418240, "used_bytes": 2684354560,
 *   "used_percent": 25.0, "bucket_bytes": 2000000000, "db_bytes": 684354560,
 *   "computed_at": "2026-08-25T12:00:00Z" | null,
 *   "counters": { "documents_processed": 0, "receipts_processed": 0,
 *                 "meetings_transcribed": 0, "members_count": 0 }
 * }
 *
 * A solução de armazenamento é ASSÍNCRONA: o snapshot (bucket_bytes) é
 * computado por fila. Quando `computed_at` é null (primeiro acesso / após
 * expirar), o snapshot ainda não foi computado e `bucket_bytes` pode vir 0
 * temporariamente. Campo opcional para retrocompatibilidade com payloads
 * que não o enviam.
 */
export interface StorageUsage {
    plan_storage_gb: number;
    total_bytes: number;
    used_bytes: number;
    used_percent: number;
    bucket_bytes: number;
    db_bytes: number;
    computed_at?: string | null;
    counters: StorageCounters;
}

/**
 * Resposta do endpoint GET /v1/dashboard/storage-usage, no envelope padrão
 * do módulo dashboard: { success, data }.
 */
export interface StorageUsageResponse {
    success: boolean;
    data: StorageUsage;
}

@Injectable({
    providedIn: 'root'
})
export class DashboardService {

    constructor(private _httpClient: HttpClient) {}

    /**
     * Get dashboard overview data (mini cards)
     */
    getOverview(): Observable<DashboardOverviewResponse> {
        return this._httpClient.get<DashboardOverviewResponse>(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.dashboard.base +
            apiResources.dashboard.overview
        );
    }

    /**
     * Get entries vs exits chart data
     * @param months Number of months (3-12)
     */
    getEntriesVsExits(months: number | string = 6): Observable<EntriesVsExitsResponse> {
        return this._httpClient.get<EntriesVsExitsResponse>(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.dashboard.base +
            apiResources.dashboard.entriesVsExits +
            '?months=' + months
        );
    }

    /**
     * Get exits by month chart data (for exits home)
     * @param months Number of months (3, 6 or 12)
     */
    getExitsByMonth(months: number | string = 6): Observable<any> {
        return this._httpClient.get(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.dashboard.base +
            apiResources.dashboard.exitsByMonth +
            '?months=' + months
        );
    }

    /**
     * Get member engagement data
     * @param month Optional month in format YYYY-MM
     */
    getMemberEngagement(month?: string): Observable<MemberEngagementResponse> {
        const url = environment.config.apiUrl +
            apiResources.v1 +
            apiResources.dashboard.base +
            '/member-engagement';

        return this._httpClient.get<MemberEngagementResponse>(
            month ? `${url}?month=${month}` : url
        );
    }

    /**
     * Get real storage usage (bucket + database + counters).
     * Endpoint: GET /v1/dashboard/storage-usage
     */
    getStorageUsage(): Observable<StorageUsageResponse> {
        return this._httpClient.get<StorageUsageResponse>(
            environment.config.apiUrl +
            apiResources.v1 +
            apiResources.dashboard.base +
            apiResources.dashboard.storageUsage
        );
    }
}
